<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="fleet-page">
    <!-- Page header -->
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Flota</h1>
      </div>
      <v-btn small outlined color="primary" @click="openPlateFinder">
        <v-icon left small>mdi-car-search</v-icon>
        Znajdź auto po tablicy
      </v-btn>
    </div>

    <!-- Znajdź / przywróć auto po tablicy (gdy zniknęło ze standardowych list) -->
    <v-dialog v-model="plateFinder.open" max-width="640px">
      <v-card>
        <v-card-title>Znajdź auto po tablicy</v-card-title>
        <v-card-text>
          <div class="d-flex align-center" style="gap: 8px;">
            <v-text-field
              v-model="plateFinder.query"
              label="Tablica rejestracyjna"
              outlined dense hide-details
              @keyup.enter="searchByPlate"
            ></v-text-field>
            <v-btn color="primary" :loading="plateFinder.searching" @click="searchByPlate">Szukaj</v-btn>
          </div>
          <div v-if="plateFinder.searched && !plateFinder.results.length" class="grey--text mt-4">
            Nie znaleziono auta z tą tablicą.
          </div>
          <div
            v-for="c in plateFinder.results"
            :key="c.id"
            class="mt-3 pa-3"
            style="display:flex; align-items:center; justify-content:space-between; gap:12px; border:1px solid #2A3142; border-radius:8px;"
          >
            <div>
              <strong>{{ c.make }} {{ c.model }} — {{ c.plate }}</strong>
              <div class="mt-1">
                <v-chip x-small :color="c.deleted ? 'error' : 'success'" text-color="white" class="mr-1">
                  {{ c.deleted ? 'Usunięte' : 'Aktywne' }}
                </v-chip>
                <v-chip x-small :color="c.isCompanyCar ? 'blue' : 'grey'" text-color="white" class="mr-1">
                  {{ c.isCompanyCar ? 'Firmowe' : 'Prywatne' }}
                </v-chip>
                <v-chip v-if="c.pendingApproval" x-small color="warning" text-color="white" class="mr-1">
                  Oczekuje akceptacji
                </v-chip>
              </div>
            </div>
            <div style="display:flex; flex-direction:column; gap:6px;">
              <v-btn
                small color="primary"
                :loading="plateFinder.restoringId === c.id"
                :disabled="!c.deleted && c.isCompanyCar && !c.pendingApproval"
                @click="restoreAsCompany(c)"
              >
                Przywróć jako firmowe
              </v-btn>
              <v-btn
                small outlined color="primary"
                :loading="plateFinder.movingId === c.id"
                :disabled="!c.deleted && c.isPrivateCar && !c.pendingApproval"
                @click="moveToDriverFleet(c)"
              >
                Do floty kierowców
              </v-btn>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="plateFinder.open = false">Zamknij</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Segmented main tabs -->
    <div class="tabs-pill mb-3" role="tablist">
      <button class="tab-pill" :class="{ 'tab-pill--active': mainTab === 0 }" @click="mainTab = 0">
        <v-icon size="14">mdi-car-multiple</v-icon>
        <span>{{ $t('fleet.cars') }}</span>
      </button>
      <button class="tab-pill" :class="{ 'tab-pill--active': mainTab === 1 }" @click="mainTab = 1">
        <v-icon size="14">mdi-garage-variant</v-icon>
        <span>{{ $t('fleet.subFleets') }}</span>
      </button>
      <button class="tab-pill" :class="{ 'tab-pill--active': mainTab === 2 }" @click="mainTab = 2">
        <v-icon size="14">mdi-archive-outline</v-icon>
        <span>Archiwum</span>
      </button>
    </div>

    <v-tabs-items v-model="mainTab" touchless>
      <v-tab-item>
        <div>

    <v-dialog v-model="dialogCreate" max-width="800px">
      <v-card>
        <v-card-title class="text-h5 primary--text"
          >{{ $t('fleet.endingRent') }}</v-card-title
        >

        <v-card-text>

          <v-container>

            <v-row dense>
              <v-col cols="12">
                <v-select
                  v-model="endRent.driverId"
                  :items="driversEndRent"
                  :label="$t('common.driver')"
                  outlined
                  required
                  item-text="name"
                  item-value="id"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="endRent.endDate"
                  :label="$t('fleet.endDate')"
                  type="date"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <!-- Zwrot po 12:00 = oddający płaci jeszcze za ten dzień. -->
                <v-text-field
                  v-model="endRent.endTime"
                  label="Godzina zwrotu"
                  type="time"
                  hint="Po 12:00 dzień zwrotu jest płatny"
                  persistent-hint
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-file-input
                  v-model="endRent.photos"
                  :label="$t('fleet.carPhotos')"
                  multiple
                ></v-file-input>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="endRent.description"
                  :label="$t('fleet.notes')"
                ></v-textarea>
              </v-col>
              <!-- Fuel level fields - conditional on car fuel type -->
              <v-col v-if="currentCarFuelType === 'DIESEL'" cols="12" md="6">
                <v-select
                  v-model="endRent.fuelLevelDiesel"
                  :items="fuelLevelOptions"
                  :label="$t('fleet.dieselLevel')"
                  outlined
                ></v-select>
              </v-col>
              <v-col v-if="currentCarFuelType === 'BENZYNA'" cols="12" md="6">
                <v-select
                  v-model="endRent.fuelLevelPetrol"
                  :items="fuelLevelOptions"
                  :label="$t('fleet.petrolLevel')"
                  outlined
                ></v-select>
              </v-col>
              <template v-if="currentCarFuelType === 'BENZYNA_LPG'">
                <v-col cols="12" md="6">
                  <v-select
                    v-model="endRent.fuelLevelPetrol"
                    :items="fuelLevelOptions"
                    :label="$t('fleet.petrolLevel')"
                    outlined
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="endRent.fuelLevelLPG"
                    :items="fuelLevelOptions"
                    :label="$t('fleet.lpgLevel')"
                    outlined
                  ></v-select>
                </v-col>
              </template>

              <v-col cols="12">
                <v-checkbox
                  v-for="item in protocol"
                  :key="item.id"
                  v-model="item.checked"
                  :label="item.label"
                ></v-checkbox>
              </v-col>
              <v-col cols="12">
                <v-btn
                  style="width: 100%"
                  color="primary"
                  @click="saveEndRent()"
                  >{{ $t('common.save') }}</v-btn
                >
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn style="width: 100%" color="red" @click="dialogCreate = false"
            >{{ $t('common.close') }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Compare Dialog -->
    <v-dialog v-model="showDialog" max-width="800" persistent>
      <v-card>
        <v-card-title>{{ $t('fleet.protocolComparison') }}</v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="6">
              <v-data-table
                :headers="comparedHeaders"
                :items="comparedTables[0]"
                item-value="name"
                class="elevation-1"
                dense
              >
                <template v-slot:item="{ item }">
                  <tr :class="getRowClass(item, 'table1')">
                    <td>{{ item.name }}</td>
                    <td>{{ item.value }}</td>
                  </tr>
                </template>
              </v-data-table>
            </v-col>

            <v-col cols="6">
              <v-data-table
                :headers="comparedHeaders"
                :items="comparedTables[1]"
                item-value="name"
                class="elevation-1"
                dense
              >
                <template v-slot:item="{ item }">
                  <tr :class="getRowClass(item, 'table2')">
                    <td>{{ item.name }}</td>
                    <td>{{ item.value }}</td>
                  </tr>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn
            @click="
              ((showDialog = false),
              (selectedTables = []),
              (comparedTables = []))
            "
            >{{ $t('common.close') }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Sub-fleet dialog -->
    <v-dialog v-model="subFleetDialog" max-width="500px">
      <v-card>
        <v-card-title>{{ subFleetEdit.id ? $t('common.edit') : $t('fleet.new') }} {{ $t('fleet.subFleet') }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="subFleetEdit.name" :label="$t('fleet.subFleetName')" outlined />
          <v-select
            v-model="subFleetEdit.parentId"
            :items="subFleetParentOptions"
            item-text="name"
            item-value="id"
            :label="$t('fleet.parentSubFleet')"
            outlined
            clearable
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="subFleetDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveSubFleet()">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Assign car to sub-fleet dialog -->
    <v-dialog v-model="assignCarDialog" max-width="500px">
      <v-card>
        <v-card-title>{{ $t('fleet.assignCarToSubFleet') }}</v-card-title>
        <v-card-text>
          <v-select
            v-model="assignCarSubFleetId"
            :items="subFleetAssignOptions"
            item-text="name"
            item-value="id"
            :label="$t('fleet.subFleet')"
            outlined
            clearable
          />
          <v-btn
            block
            outlined
            color="orange"
            class="mt-2"
            @click="assignCarSubFleetId = null; assignCarToSubFleet()"
          >
            <v-icon small left>mdi-home</v-icon> {{ $t('fleet.assignToMainFleet') }}
          </v-btn>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="assignCarDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="assignCarToSubFleet()">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Sub-fleet detail dialog -->
    <v-dialog v-model="subFleetDetailDialog" max-width="900px" scrollable>
      <v-card>
        <v-card-title class="text-h5 primary--text">
          <v-icon left>mdi-folder-open</v-icon>
          {{ subFleetDetail.name || $t('fleet.subFleet') }}
          <v-spacer />
          <v-chip
            small
            :color="subFleetDetail.status === 'ACTIVE' ? 'success' : subFleetDetail.status === 'SUSPENDED' ? 'warning' : 'error'"
            class="mr-2"
          >
            {{ subFleetDetail.status || 'ACTIVE' }}
          </v-chip>
          <v-btn icon @click="subFleetDetailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div class="tabs-pill mb-3" style="margin-top: 4px;" role="tablist">
            <button class="tab-pill" :class="{ 'tab-pill--active': subFleetDetailTab === 0 }" @click="subFleetDetailTab = 0">
              <v-icon size="14">mdi-account</v-icon><span>{{ $t('fleet.ownerData') }}</span>
            </button>
            <button class="tab-pill" :class="{ 'tab-pill--active': subFleetDetailTab === 1 }" @click="subFleetDetailTab = 1">
              <v-icon size="14">mdi-currency-usd</v-icon><span>{{ $t('fleet.fees') }}</span>
            </button>
            <button class="tab-pill" :class="{ 'tab-pill--active': subFleetDetailTab === 2 }" @click="subFleetDetailTab = 2">
              <v-icon size="14">mdi-calculator</v-icon><span>{{ $t('fleet.settlement') }}</span>
            </button>
            <button class="tab-pill" :class="{ 'tab-pill--active': subFleetDetailTab === 3 }" @click="subFleetDetailTab = 3">
              <v-icon size="14">mdi-car</v-icon><span>{{ $t('fleet.carsAndDrivers') }}</span>
            </button>
          </div>

          <v-tabs-items v-model="subFleetDetailTab" touchless>
            <!-- Owner Info Tab -->
            <v-tab-item>
              <v-container class="pt-4">
                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="subFleetDetail.ownerName"
                      :label="$t('fleet.ownerFullName')"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="subFleetDetail.ownerCompanyName"
                      :label="$t('fleet.companyName')"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12">
                    <v-text-field
                      v-model="subFleetDetail.ownerAddress"
                      :label="$t('fleet.address')"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="subFleetDetail.ownerPhone"
                      :label="$t('fleet.phone')"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="subFleetDetail.ownerEmail"
                      label="Email"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="subFleetDetail.ownerNip"
                      label="NIP"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="subFleetDetail.ownerPesel"
                      label="PESEL"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="subFleetDetail.ownerBankAccount"
                      :label="$t('fleet.bankAccountNo')"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="subFleetDetail.status"
                      :items="subFleetStatuses"
                      label="Status"
                      outlined
                      dense
                    />
                  </v-col>
                </v-row>
                <v-btn color="primary" @click="saveSubFleetDetail()" :loading="subFleetDetailSaving">
                  <v-icon left small>mdi-content-save</v-icon> {{ $t('fleet.saveOwnerData') }}
                </v-btn>
              </v-container>
            </v-tab-item>

            <!-- Fee Configuration Tab -->
            <v-tab-item>
              <v-container class="pt-4">
                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="subFleetDetail.feeType"
                      :items="feeTypeOptions"
                      :label="$t('fleet.feeType')"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="subFleetDetail.feeValue"
                      :label="'Wartosc oplaty (' + feeValueSuffix + ')'"
                      type="number"
                      outlined
                      dense
                      :suffix="feeValueSuffix"
                    />
                  </v-col>
                  <v-col cols="12" md="6" v-if="subFleetDetail.feeType === 'MIXED'">
                    <v-text-field
                      v-model="subFleetDetail.feeFixedBase"
                      :label="$t('fleet.fixedBaseAmount')"
                      type="number"
                      outlined
                      dense
                      suffix="PLN"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="subFleetDetail.feePeriod"
                      :items="feePeriodOptions"
                      :label="$t('fleet.billingPeriod')"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="subFleetDetail.feeDayOfMonth"
                      :label="$t('fleet.dayOfMonth')"
                      type="number"
                      outlined
                      dense
                      min="1"
                      max="28"
                    />
                  </v-col>
                </v-row>
                <v-btn color="primary" @click="saveSubFleetDetail()" :loading="subFleetDetailSaving">
                  <v-icon left small>mdi-content-save</v-icon> {{ $t('fleet.saveFeeConfig') }}
                </v-btn>
              </v-container>
            </v-tab-item>

            <!-- Settlement Preview Tab -->
            <v-tab-item>
              <v-container class="pt-4">
                <v-row dense align="center">
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="settlementMonth"
                      :items="monthOptions"
                      :label="$t('fleet.month')"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="settlementYear"
                      :label="$t('fleet.year')"
                      type="number"
                      outlined
                      dense
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-btn
                      color="accent"
                      @click="calculateSettlement()"
                      :loading="settlementLoading"
                    >
                      <v-icon left small>mdi-calculator</v-icon> {{ $t('fleet.calculateSettlement') }}
                    </v-btn>
                  </v-col>
                </v-row>

                <v-card v-if="settlementData" outlined class="mt-4 pa-4" color="secondary">
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <div class="caption grey--text">{{ $t('fleet.totalRent') }}</div>
                      <div class="text-h6 success--text">
                        {{ formatCurrency(settlementData.totalRent) }}
                      </div>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="caption grey--text">{{ $t('fleet.btpFee') }}</div>
                      <div class="text-h6 warning--text">
                        {{ formatCurrency(settlementData.btpFee) }}
                      </div>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="caption grey--text">{{ $t('fleet.ownerPayout') }}</div>
                      <div class="text-h6 accent--text">
                        {{ formatCurrency(settlementData.netPayout) }}
                      </div>
                    </v-col>
                  </v-row>
                  <v-divider class="my-3" />
                  <v-row dense v-if="settlementData.details">
                    <v-col cols="12">
                      <div class="caption grey--text mb-2">{{ $t('fleet.details') }}</div>
                      <v-simple-table dense>
                        <thead>
                          <tr>
                            <th>{{ $t('fleet.item') }}</th>
                            <th class="text-right">{{ $t('fleet.amount') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(detail, idx) in settlementData.details" :key="idx">
                            <td>{{ detail.label }}</td>
                            <td class="text-right">{{ formatCurrency(detail.amount) }}</td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </v-col>
                  </v-row>
                </v-card>

                <v-alert v-if="settlementError" type="error" class="mt-4" dense outlined>
                  {{ settlementError }}
                </v-alert>
              </v-container>
            </v-tab-item>

            <!-- Cars & Drivers Tab -->
            <v-tab-item>
              <v-container class="pt-4">
                <v-row dense align="center" class="mb-2">
                  <v-col cols="12" md="8">
                    <v-autocomplete
                      v-model="subFleetAddCarId"
                      :items="availableCarsForSubFleet"
                      item-text="label"
                      item-value="id"
                      label="Dodaj auto do tej podfloty"
                      hint="Wybierz auto z innej podfloty lub z głównej floty"
                      persistent-hint
                      outlined
                      dense
                      clearable
                      prepend-inner-icon="mdi-car-arrow-right"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-btn
                      color="primary"
                      :disabled="!subFleetAddCarId"
                      :loading="subFleetAddCarLoading"
                      @click="addCarToCurrentSubFleet()"
                      block
                    >
                      <v-icon left small>mdi-plus</v-icon>Dodaj auto
                    </v-btn>
                  </v-col>
                </v-row>
                <v-data-table
                  :headers="subFleetDetailCarsHeaders"
                  :items="subFleetDetailCars"
                  dense
                  class="elevation-1"
                  :no-data-text="$t('fleet.noCarsAssigned')"
                >
                  <template v-slot:item.driverName="{ item }">
                    {{ item.driverName || '-' }}
                  </template>
                  <template v-slot:item.actions="{ item }">
                    <v-btn x-small outlined color="error" @click="removeCarFromCurrentSubFleet(item.id)">
                      <v-icon x-small left>mdi-close</v-icon>Odepnij
                    </v-btn>
                  </template>
                </v-data-table>
              </v-container>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Trigger Button for Dialog -->
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="filteredCars"
          class="elevation-1"
          :loading="loading"
          :search="search"
          :items-per-page="10"
          :footer-props="{ 'items-per-page-options': [10, 25, 50, 100, -1] }"
        >
          <template v-slot:top>
            <div class="table-section-title">
              <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-car-multiple</v-icon></div>
              <div class="filter-title">
                {{ $t('fleet.title') }}
                <span v-if="activeSubFleetName" class="filter-chip" @click="clearSubFleetFilter()">
                  {{ activeSubFleetName }}
                  <v-icon size="14">mdi-close</v-icon>
                </span>
              </div>
              <button
                v-if="subFleets.length === 0"
                class="action-pill action-pill--neutral"
                @click="openSubFleetDialog()"
              >
                <v-icon size="14">mdi-folder-plus</v-icon>{{ $t('fleet.newSubFleet') }}
              </button>
              <button
                class="action-pill action-pill--primary"
                @click="dialog = true; action = 'Tworzenie'; currentCarId = null"
              >
                <v-icon size="14">mdi-plus</v-icon>{{ $t('common.add') }}
              </button>
              <button
                class="action-pill action-pill--neutral"
                :disabled="boltSyncing"
                @click="syncFromBolt"
              >
                <v-icon size="14">mdi-sync</v-icon>Synchronizuj z Bolta
              </button>
              <button
                class="action-pill action-pill--neutral"
                :disabled="boltPairing"
                @click="pairDriversFromBolt"
              >
                <v-icon size="14">mdi-account-switch</v-icon>Sparuj auta z kierowcami
              </button>
            </div>
            <div class="search-row">
              <div class="search-input">
                <v-icon size="18" color="#6E7691">mdi-magnify</v-icon>
                <input v-model="search" type="text" :placeholder="$t('common.search')" />
              </div>
            </div>
          </template>
          <template v-slot:item.subFleetName="{ item }">
            <v-chip v-if="item.subFleetName" x-small>{{ item.subFleetName }}</v-chip>
            <span v-else style="color: #6E7691;">—</span>
          </template>
          <template v-slot:item.details="{ item }">
            <button class="row-btn row-btn--warning" @click="infoCar(item.id)" :title="$t('common.edit')">
              <v-icon size="14">mdi-pencil</v-icon>
            </button>
          </template>

          <template v-slot:item.historyCar="{ item }">
            <button class="row-btn row-btn--info" @click="historyCar(item.id)" title="Historia">
              <v-icon size="14">mdi-history</v-icon>
            </button>
          </template>
          <template v-slot:item.endRent="{ item }">
            <v-btn class="error" @click="endRentShowModal(item.id)"
              ><v-icon small class="pt-1">mdi-cancel</v-icon></v-btn
            >
          </template>
          <template v-slot:item.assignSubFleet="{ item }">
            <v-btn x-small outlined color="primary" @click="openAssignCarDialog(item.id)">
              <v-icon x-small left>mdi-folder-move</v-icon> {{ $t('fleet.subFleet') }}
            </v-btn>
          </template>
          <template v-slot:item.inspection="{ item }">
            <v-btn small color="red darken-1" dark @click="openInspection(item)" title="Inspekcja auta">
              <v-icon small left>mdi-clipboard-check-outline</v-icon>Inspekcja
            </v-btn>
            <v-btn v-if="isOwner" x-small outlined color="amber darken-3" class="ml-1" @click="openOdoEdit(item)" title="Popraw przebieg (head admin)">
              <v-icon x-small left>mdi-counter</v-icon>Przebieg
            </v-btn>
            <v-btn v-if="isOwner" x-small outlined color="amber darken-3" class="ml-1" @click="editVin(item)" title="Popraw VIN (head admin)">
              <v-icon x-small left>mdi-identifier</v-icon>VIN
            </v-btn>
          </template>
          <template v-slot:item.deleteCar="{ item }">
            <v-btn class="error" @click="deleteCar(item.id)"
              ><v-icon small class="pt-1">mdi-delete</v-icon></v-btn
            >
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Korekta przebiegu auta (head admin) -->
    <v-dialog v-model="odoEdit.open" max-width="420px">
      <v-card v-if="odoEdit.car">
        <v-card-title>
          <v-icon left color="amber darken-3">mdi-counter</v-icon>
          Popraw przebieg — {{ odoEdit.car.plate }}
        </v-card-title>
        <v-card-text>
          <div class="caption grey--text mb-2">{{ odoEdit.car.make }} {{ odoEdit.car.model }}. Aktualny licznik widoczny we Flocie i garażu.</div>
          <v-text-field
            v-model.number="odoEdit.value"
            type="number"
            label="Aktualny przebieg"
            suffix="km"
            prepend-inner-icon="mdi-counter"
            outlined dense hide-details
          ></v-text-field>
          <div class="caption grey--text mt-1">Można obniżyć, jeśli wcześniej wpisano za dużo. Zmiana trafia do logu audytu.</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="odoEdit.open = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="odoEdit.saving" @click="saveOdo">Zapisz</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Inspekcja auta — status, historia, akceptacja, wykonaj sam -->
    <v-dialog v-model="inspection.open" max-width="820px" scrollable>
      <v-card v-if="inspection.car">
        <v-card-title class="d-flex align-center">
          <v-icon left color="primary">mdi-clipboard-check-outline</v-icon>
          Inspekcja auta — {{ inspection.car.make }} {{ inspection.car.model }} ({{ inspection.car.plate }})
          <v-spacer></v-spacer>
          <v-btn icon @click="inspection.open = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <!-- Status -->
          <v-alert
            v-if="inspection.status"
            :type="inspection.status.isOverdue ? 'warning' : 'success'"
            outlined dense class="mb-4"
          >
            <div v-if="inspection.status.lastInspectionAt">
              Ostatnia inspekcja: <b>{{ formatInspDate(inspection.status.lastInspectionAt) }}</b>
              ({{ inspStatusLabel(inspection.status.lastStatus) }}) ·
              następna do: <b>{{ formatInspDate(inspection.status.nextDueAt) }}</b>
            </div>
            <div v-else>Brak inspekcji — auto nigdy nie było inspekcjonowane.</div>
            <div v-if="inspection.status.isOverdue" class="mt-1">
              <b>Zaległa o {{ inspection.status.daysOverdue }} dni.</b>
              <span v-if="inspection.status.blockPayout"> Wypłaty kierowcy są zablokowane.</span>
            </div>
            <div v-if="inspection.status.requested" class="mt-1">
              <b>Zażądano inspekcji od kierowcy</b> — oczekuje na wykonanie w aplikacji kierowcy.
            </div>
          </v-alert>

          <!-- Wykonaj inspekcję (admin, auto lokalne) -->
          <div class="mb-4">
            <v-btn small color="primary" outlined @click="inspection.showPerform = !inspection.showPerform">
              <v-icon left small>mdi-plus</v-icon>Wykonaj inspekcję (na miejscu)
            </v-btn>
            <v-btn
              small color="indigo" outlined class="ml-2"
              :loading="inspection.requesting"
              :disabled="inspection.status && inspection.status.requested"
              @click="requestInspection"
              title="Poproś kierowcę o wykonanie inspekcji w aplikacji"
            >
              <v-icon left small>mdi-clipboard-alert-outline</v-icon>Zażądaj inspekcji od kierowcy
            </v-btn>
            <v-card v-if="inspection.showPerform" outlined class="mt-2 pa-3">
              <div class="caption grey--text mb-2">Sprawdź stan — zaznacz pozycje OK, opisz uwagi:</div>
              <v-text-field v-model.number="inspection.performOdometer" type="number" label="Przebieg (opcjonalnie)" suffix="km" outlined dense hide-details class="mb-2" />
              <div v-for="it in inspectionItems" :key="it.key" class="d-flex align-center mt-2" style="gap:8px; flex-wrap:wrap;">
                <span class="body-2" style="min-width:150px;">{{ it.label }}</span>
                <v-btn
                  v-for="opt in inspectionRatings"
                  :key="opt.value"
                  x-small
                  :color="opt.color"
                  :outlined="inspection.perform[it.key] !== opt.value"
                  :dark="inspection.perform[it.key] === opt.value"
                  class="mr-1"
                  @click="$set(inspection.perform, it.key, opt.value)"
                >{{ opt.label }}</v-btn>
              </div>
              <div class="mt-3 d-flex" style="gap:6px; flex-wrap:wrap; align-items:center;">
                <div v-for="(p, i) in inspection.performPreviews" :key="i" style="position:relative;">
                  <img :src="p" style="height:56px; width:56px; object-fit:cover; border-radius:6px; border:1px solid #2A3142;" />
                  <button type="button" @click="removePerformPhoto(i)" style="position:absolute; top:-6px; right:-6px; background:#EF5350; border:0; border-radius:50%; width:18px; height:18px; color:#fff; cursor:pointer; line-height:1;">×</button>
                </div>
                <v-btn small outlined @click="$refs.performPhotoInput.click()"><v-icon small left>mdi-camera-plus</v-icon>Zdjęcia</v-btn>
                <input ref="performPhotoInput" type="file" accept="image/*" multiple style="display:none" @change="onPerformPhotos" />
              </div>
              <v-textarea v-model="inspection.performNote" label="Uwagi (opcjonalnie)" outlined dense rows="2" hide-details class="mt-3" />
              <div class="mt-3 d-flex">
                <v-btn small color="primary" :loading="inspection.saving" @click="submitAdminInspection">Zapisz inspekcję</v-btn>
                <v-btn small text @click="inspection.showPerform = false" class="ml-2">Anuluj</v-btn>
              </div>
            </v-card>
          </div>

          <!-- Historia -->
          <div class="section-label mb-2" style="font-weight:700; color:#6E7691; text-transform:uppercase; font-size:12px;">Historia inspekcji</div>
          <div v-if="!inspection.history.length" class="grey--text">Brak inspekcji.</div>
          <v-card v-for="insp in inspection.history" :key="insp.id" outlined class="mb-2 pa-3">
            <div class="d-flex align-center">
              <v-chip x-small :color="inspStatusColor(insp.status)" text-color="white" class="mr-2">{{ inspStatusLabel(insp.status) }}</v-chip>
              <span>{{ formatInspDate(insp.createdAt) }}</span>
              <span class="grey--text ml-2" v-if="insp.driver">· {{ insp.driver.firstName }} {{ insp.driver.lastName }} (kierowca)</span>
              <span class="grey--text ml-2" v-else-if="insp.admin">· {{ insp.admin.firstName }} {{ insp.admin.lastName }} (admin)</span>
              <v-spacer></v-spacer>
              <template v-if="insp.status === 'PENDING_REVIEW'">
                <v-btn x-small color="success" :loading="inspection.reviewingId === insp.id" @click="reviewInspection(insp.id, 'ACCEPT')" class="mr-1">Akceptuj</v-btn>
                <v-btn x-small color="error" :loading="inspection.reviewingId === insp.id" @click="reviewInspection(insp.id, 'FLAG')">Oflaguj</v-btn>
              </template>
            </div>
            <div v-if="inspMileage(insp)" class="mt-1 caption">Przebieg: {{ inspMileage(insp) }} km</div>
            <div v-if="inspRatings(insp).length" class="mt-2 d-flex" style="gap:6px; flex-wrap:wrap;">
              <v-chip v-for="(r, i) in inspRatings(insp)" :key="i" x-small outlined :color="r.color">
                {{ r.name }}: <b class="ml-1">{{ r.label }}</b>
              </v-chip>
            </div>
            <div v-if="insp.note" class="mt-1 caption">Uwagi kierowcy: {{ insp.note }}</div>
            <div v-if="insp.reviewNote" class="mt-1 caption">Uwagi admina: {{ insp.reviewNote }}</div>
            <div v-if="insp.photos && insp.photos.length" class="mt-2 d-flex" style="gap:6px; flex-wrap:wrap;">
              <a v-for="(p, i) in insp.photos" :key="i" v-show="inspection.photoUrls[p]" :href="inspection.photoUrls[p]" target="_blank">
                <img :src="inspection.photoUrls[p]" style="height:64px; width:64px; object-fit:cover; border-radius:6px; border:1px solid #2A3142;" />
              </a>
            </div>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>
  
    <!-- Dialog -->
    <v-dialog v-model="dialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t('fleet.vehicleForm') }}</span>
        </v-card-title>

        <v-card-text>
          <!-- Form -->
          <v-form ref="form" v-model="valid" @submit.prevent="submitForm">
            <v-row>
              <!-- Make -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.make"
                  :label="$t('fleet.make')"
                  outlined
                  required
                />
              </v-col>

              <!-- Model -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.model"
                  label="Model"
                  outlined
                  required
                />
              </v-col>

              <!-- Color -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.color"
                  :label="$t('fleet.color')"
                  outlined
                  required
                />
              </v-col>
              <!-- Plate -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.plate"
                  :label="$t('fleet.plate')"
                  outlined
                  required
                />
              </v-col>

              <!-- Production Year -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.productionYear"
                  :label="$t('fleet.productionYear')"
                  type="number"
                  outlined
                  required
                />
              </v-col>
              <!-- Production Year -->
              <!-- Last Service Date -->
              <v-col cols="12" md="6">
                <v-menu
                  v-model="menu.lastService"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formData.lastService"
                      :label="$t('fleet.serviceDate')"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                    />
                  </template>
                  <v-date-picker
                    v-model="formData.lastService"
                    @input="menu.lastService = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <!-- Insurance Valid To -->
              <v-col cols="12" md="6">
                <v-menu
                  v-model="menu.insuranceValidTo"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formData.insuranceValidTo"
                      :label="$t('fleet.insuranceDate')"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                    />
                  </template>
                  <v-date-picker
                    v-model="formData.insuranceValidTo"
                    @input="menu.insuranceValidTo = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <!-- Insurance Policy Number -->
              <v-col cols="12" md="6">
                <v-text-field v-model="formData.insurancePolicyNumber" :label="$t('fleet.policyNumber')" dense outlined />
              </v-col>

              <!-- Insurer Name -->
              <v-col cols="12" md="6">
                <v-text-field v-model="formData.insurerName" :label="$t('fleet.insurerName')" dense outlined />
              </v-col>

              <!-- Inspection Valid To -->
              <v-col cols="12" md="6">
                <v-menu
                  v-model="menu.inspectionValidTo"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="formData.inspectionValidTo"
                      :label="$t('fleet.inspectionDate')"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                    />
                  </template>
                  <v-date-picker
                    v-model="formData.inspectionValidTo"
                    @input="menu.inspectionValidTo = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>

              <!-- Wrap Type -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="formData.wrapType"
                  :items="wrapTypes"
                  :label="$t('fleet.wrapType')"
                  outlined
                  required
                />
              </v-col>

              <!-- Advance -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.advance"
                  :label="$t('fleet.deposit')"
                  type="number"
                  outlined
                  required
                />
              </v-col>

              <!-- Rent Price -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.rentPrice"
                  :label="$t('fleet.rentCost')"
                  type="number"
                  outlined
                  required
                />
              </v-col>

              <!-- Odometer -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.odometer"
                  :label="$t('fleet.odometer')"
                  type="number"
                  outlined
                  required
                />
              </v-col>

              <!-- Odometer -->
              <v-col cols="12" md="12">
                <v-text-field
                  v-model="formData.previousServiceOdometer"
                  :label="$t('fleet.previousOdometer')"
                  type="number"
                  outlined
                  required
                />
              </v-col>

              <!-- Rodzaj paliwa -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="formData.fuelType"
                  :items="fuelTypes"
                  :label="$t('fleet.fuelType')"
                  outlined
                />
              </v-col>

              <!-- Nr boczny -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.sideNumber"
                  :label="$t('fleet.sideNumber')"
                  outlined
                />
              </v-col>

              <v-col cols="12" md="12">
                <v-checkbox
                  v-model="formData.isCompanyCar"
                  :label="$t('fleet.companyCar')"
                  outlined
                  required
                />
              </v-col>
              <!-- Car ID (Select) -->
              <v-col cols="12">
                <v-select
                  v-model="formData.cardId"
                  :items="fuelCards"
                  item-text="name"
                  item-value="id"
                  :label="$t('fleet.fuelCard')"
                  outlined
                  :rules="[validateOptionalUUID]"
                  return-object
                />
              </v-col>
                          <v-col cols="12" md="12">
                <v-select
                  v-model="formData.departmentId"
                  :items="subCompanies"
                  item-text="name"
                  item-value="id"
                  :label="$t('fleet.branch')"
                   outlined
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <!-- Actions -->
        <v-card-actions>
          <v-spacer></v-spacer>
          <!-- Cancel Button -->
          <v-btn text color="secondary" @click="dialog = false">{{ $t('common.cancel') }}</v-btn>
          <!-- Submit Button -->
          <v-btn color="primary" @click="submitForm" :disabled="!valid">
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
        </div>
      </v-tab-item>

      <v-tab-item>
        <SubFleetPage />
      </v-tab-item>

      <!-- Archiwum floty firmowej (usunięte auta) -->
      <v-tab-item>
        <v-row>
          <v-col cols="12">
            <v-data-table
              :headers="archiveHeaders"
              :items="archiveCars"
              class="elevation-1"
              :loading="archiveLoading"
              :search="archiveSearch"
              :items-per-page="10"
              :footer-props="{ 'items-per-page-options': [10, 25, 50, 100, -1] }"
            >
              <template v-slot:top>
                <div class="table-section-title">
                  <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-archive-outline</v-icon></div>
                  <div class="filter-title">Archiwum pojazdów</div>
                </div>
                <div class="search-row">
                  <div class="search-input">
                    <v-icon size="18" color="#6E7691">mdi-magnify</v-icon>
                    <input v-model="archiveSearch" type="text" :placeholder="$t('common.search')" />
                  </div>
                </div>
              </template>
              <template v-slot:item.restore="{ item }">
                <v-btn small color="success" @click="restoreCar(item.id)">
                  <v-icon left small>mdi-archive-arrow-up</v-icon>Przywróć
                </v-btn>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>

    <!-- Wybór aut z Bolta do dodania -->
    <v-dialog v-model="boltDialog" max-width="960" scrollable>
      <v-card>
        <v-card-title>
          Auta z Bolta do dodania
          <v-spacer></v-spacer>
          <span class="text-body-2 grey--text">Odznacz te, których nie chcesz dodawać</span>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text style="max-height:65vh">
          <v-select
            v-model="boltDepartmentId"
            :items="subCompanies"
            item-text="name"
            item-value="id"
            label="Przypisz do oddziału"
            clearable
            dense
            outlined
            hide-details
            class="mb-3"
            style="max-width:400px"
          ></v-select>
          <v-data-table
            v-model="boltSelected"
            :headers="boltHeaders"
            :items="boltVehicles"
            item-key="id"
            show-select
            :items-per-page="25"
            dense
          >
            <template v-slot:[`item.year`]="{ item }">{{ item.year || '-' }}</template>
            <template v-slot:[`item.vin`]="{ item }">{{ item.vin || '-' }}</template>
            <template v-slot:[`item.color`]="{ item }">{{ item.color || '-' }}</template>
          </v-data-table>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <span class="text-body-2">Wybrano: {{ boltSelected.length }} / {{ boltVehicles.length }}</span>
          <v-spacer></v-spacer>
          <v-btn text @click="boltDialog = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="boltCreating" :disabled="!boltSelected.length" @click="confirmBoltImport">
            <v-icon left>mdi-plus</v-icon>Dodaj wybrane ({{ boltSelected.length }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import { translateError } from "@/services/translateError";
const axios = require("axios");
import * as dayjs from "dayjs";
import SubFleetPage from "./SubFleetPage.vue";
export default {
  components: { SubFleetPage },
  data() {
    return {
      // Wyszukiwarka/naprawa auta po tablicy (gdy "zniknęło" z list).
      plateFinder: { open: false, query: '', results: [], searched: false, searching: false, restoringId: null, movingId: null },
      // Korekta przebiegu auta (head admin).
      odoEdit: { open: false, car: null, value: null, saving: false },
      // Inspekcja auta (status/historia/akceptacja/wykonaj sam).
      inspection: { open: false, car: null, status: null, history: [], showPerform: false, perform: {}, performNote: '', performOdometer: null, performPhotos: [], performPreviews: [], photoUrls: {}, saving: false, reviewingId: null, requesting: false },
      inspectionItems: [
        { key: 'exterior', label: 'Nadwozie (rysy, wgniecenia)' },
        { key: 'interior', label: 'Wnętrze' },
        { key: 'lights', label: 'Światła' },
        { key: 'tires', label: 'Opony' },
        { key: 'documents', label: 'Dokumenty (dowód, OC)' },
        { key: 'cleanliness', label: 'Czystość' },
      ],
      inspectionRatings: [
        { value: 'VERY_GOOD', label: 'Bardzo dobry', color: 'green darken-1' },
        { value: 'GOOD', label: 'Dobry', color: 'amber darken-2' },
        { value: 'BAD', label: 'Zły', color: 'red darken-1' },
      ],
      dialogCreate: false,
      endRent: [],
      currentCarFuelType: null,
      fuelLevelOptions: ['E', '1/4', '1/2', '3/4', 'F'],
      driversEndRent: [],
      protocol: [
        { id: 1, label: "Check tires", checked: false },
        { id: 2, label: "Check engine", checked: false },
      ],
      comparedHeaders: [
        { title: "Nazwa", value: "name" },
        { title: "Wartość", value: "value" },
      ],
      comparedTables: [],
      selectedTables: [],
      showDialog: false,
      currentCarId: null,
      totalItems: 0,
      dialog: false, // Dialog visibility
      valid: false, // Form validity
      carHistoryValid: false,
      dialogHistory: false,
      search: "",
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      cars: [],
      fuelCards: [],
      drivers: [],
      headers: [
        { text: "Marka", value: "make", align: "start", sortable: true },
        { text: "Model", value: "model", align: "start", sortable: true },
        { text: "Rejestracja", value: "plate", align: "start", sortable: true },
        { text: "Pierwszy Kierowca", value: "firstDriver", align: "start", sortable: true },
        { text: "Drugi Kierowca", value: "secondDriver", align: "start", sortable: true },
        { text: "Nr polisy", value: "insurancePolicyNumber", sortable: true },
        { text: "Ubezpieczalnia", value: "insurerName", sortable: true },
        { text: "Nr boczny", value: "sideNumber", sortable: true },
        { text: "Podflota", value: "subFleetName", sortable: true },
        { text: "", value: "inspection", sortable: false },
        { text: "", value: "assignSubFleet", sortable: false },
        { text: "", value: "details", sortable: false },
        { text: "", value: "historyCar", sortable: false },
        { text: "", value: "endRent", sortable: false },
        { text: "", value: "deleteCar", sortable: false },
      ],
      archiveCars: [],
      archiveLoading: false,
      archiveSearch: "",
      archiveHeaders: [
        { text: "Marka", value: "make", align: "start", sortable: true },
        { text: "Model", value: "model", align: "start", sortable: true },
        { text: "Rejestracja", value: "plate", align: "start", sortable: true },
        { text: "Nr boczny", value: "sideNumber", sortable: true },
        { text: "", value: "restore", sortable: false },
      ],
      carHistory: [
        // Example history data
        {
          date: "2024-11-01",
          description: "Oil change",
          type: "Maintenance",
          metadata: [
            { name: "Cost", value: "€50" },
            { name: "Service Center", value: "AutoFix" },
          ],
          image: null,
          color: "green",
          icon: "mdi-wrench",
        },
      ],
      newHistory: {
        description: "",
        historyRecordType: "",
        image: null,
        metadata: [],
      },
      metadataField: {
        name: "",
        value: "",
      },
      loading: false,
      predefinedMetadataFields: [], // Dynamic fields
      predefinedMetadata: {}, // Dynamic field values
      historyRecordTypes: ["MILEAGE", "CRASH", "SERVICE", "OTHER", "END_OF_RENT", "START_OF_RENT"], // Example record types
      metadataTemplates: {
        Maintenance: [
          { name: "cost", label: "Cost (€)" },
          { name: "serviceCenter", label: "Service Center" },
        ],
        Repair: [
          { name: "repairCost", label: "Repair Cost (€)" },
          { name: "repairShop", label: "Repair Shop" },
        ],
        Incident: [
          { name: "incidentDate", label: "Incident Date" },
          { name: "reportedToInsurance", label: "Reported to Insurance" },
        ],
      },
      menu: {
        lastService: false,
        insuranceValidTo: false,
        inspectionValidTo: false,
      },
      formData: {
        make: "",
        model: "",
        color: "",
        plate: "",
        isCompanyCar: true,
        productionYear: null,
        lastService: null,
        insuranceValidTo: null,
        inspectionValidTo: null,
        wrapType: null,
        advance: null,
        rentPrice: null,
        odometer: null,
        previousServiceOdometer: null,
        sideNumber: null,
        insurancePolicyNumber: null,
        insurerName: null,
        fuelType: "BENZYNA",
        cardId: null,
        departmentId: null,
      },
      plate: "",
      subCompanies:[],
      // Import / parowanie aut z Bolta (przeniesione z Wykazu pojazdów).
      boltSyncing: false,
      boltCreating: false,
      boltPairing: false,
      boltDialog: false,
      boltVehicles: [],
      boltSelected: [],
      boltDepartmentId: null,
      boltHeaders: [
        { text: "Nr rej.", value: "reg_number" },
        { text: "Model", value: "model" },
        { text: "Rok", value: "year" },
        { text: "VIN", value: "vin" },
        { text: "Kolor", value: "color" },
      ],
      wrapTypes: ["BOLT", "UBER", "CUSTOM", "FREENOW", "NONE"],
      fuelTypes: [
        { text: "Diesel", value: "DIESEL" },
        { text: "Benzyna", value: "BENZYNA" },
        { text: "Benzyna + LPG", value: "BENZYNA_LPG" },
      ],
      // Sub-fleets
      subFleets: [],
      subFleetDialog: false,
      subFleetEdit: { id: null, name: "", parentId: null },
      activeSubFleetId: null,
      assignCarDialog: false,
      assignCarId: null,
      assignCarSubFleetId: null,
      // Sub-fleet detail
      subFleetDetailDialog: false,
      subFleetDetail: {},
      subFleetDetailTab: 0,
      subFleetDetailSaving: false,
      mainTab: 0,
      subFleetDetailCars: [],
      subFleetAddCarId: null,
      subFleetAddCarLoading: false,
      subFleetCarsHeaders: [
        { text: 'Rejestracja', value: 'plate' },
        { text: 'Marka', value: 'brand' },
        { text: 'Model', value: 'model' },
        { text: 'VIN', value: 'vin' },
        { text: 'Kierowca', value: 'driverName' },
      ],
      subFleetDetailCarsHeaders: [
        { text: 'Rejestracja', value: 'plate' },
        { text: 'Marka', value: 'make' },
        { text: 'Model', value: 'model' },
        { text: 'VIN', value: 'vin' },
        { text: 'Kierowca', value: 'driverName' },
        { text: '', value: 'actions', align: 'end', sortable: false, width: 120 },
      ],
      feeTypeOptions: [
        { text: 'Procentowa', value: 'PERCENTAGE' },
        { text: 'Stała kwota', value: 'FIXED' },
        { text: 'Mieszana', value: 'MIXED' },
      ],
      feePeriodOptions: [
        { text: 'Miesięczna', value: 'MONTHLY' },
        { text: 'Tygodniowa', value: 'WEEKLY' },
      ],
      statusOptions: [
        { text: 'Aktywna', value: 'ACTIVE' },
        { text: 'Zawieszona', value: 'SUSPENDED' },
        { text: 'Zamknięta', value: 'CLOSED' },
      ],
      settlementYear: new Date().getFullYear(),
      settlementMonth: new Date().getMonth() + 1,
      settlementResult: null,
      settlementLoading: false,
    };
  },
  watch: {
    search: {
      handler() {
        this.initialize();
      },
      deep: true,
    },
    mainTab(val) {
      // Wczytaj archiwum przy wejściu w zakładkę Archiwum.
      if (val === 2) this.fetchArchive();
    },
  },
  computed: {
    // Head admin = właściciel platformy (z tokenu: actualRole/isHead/hasAccessToAllCompanies).
    isOwner() {
      try {
        const t = localStorage.getItem('token');
        if (!t) return false;
        const p = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return p.actualRole === 'headadmin' || p.isHead === true || p.hasAccessToAllCompanies === true;
      } catch (e) {
        return false;
      }
    },
    settlementData() {
      const r = this.settlementResult;
      if (!r) return null;
      const details = [];
      if (Array.isArray(r.perCar)) {
        for (const c of r.perCar) {
          details.push({ label: `Najem ${c.plate}`, amount: c.rentCollected });
          details.push({ label: `Prowizja ${c.plate}`, amount: -c.commission });
        }
      }
      return {
        totalRent: r.totalRentCollected || 0,
        btpFee: r.feeAmount || 0,
        netPayout: r.netPayoutToOwner || 0,
        details,
      };
    },
    filteredCars() {
      if (!this.activeSubFleetId) return this.cars;
      const ids = this.getSubFleetIdsRecursive(this.activeSubFleetId);
      return this.cars.filter(c => ids.includes(c.subFleetId));
    },
    activeSubFleetName() {
      if (!this.activeSubFleetId) return null;
      const sf = this.subFleets.find(s => s.id === this.activeSubFleetId);
      return sf ? sf.name : null;
    },
    subFleetTree() {
      const map = {};
      const roots = [];
      this.subFleets.forEach(sf => {
        map[sf.id] = { ...sf, children: [], carCount: (sf.cars || []).length };
      });
      this.subFleets.forEach(sf => {
        if (sf.parentId && map[sf.parentId]) {
          map[sf.parentId].children.push(map[sf.id]);
        } else {
          roots.push(map[sf.id]);
        }
      });
      return roots;
    },
    subFleetParentOptions() {
      return this.subFleets
        .filter(sf => sf.id !== this.subFleetEdit.id)
        .map(sf => ({ id: sf.id, name: sf.name }));
    },
    subFleetAssignOptions() {
      return this.subFleets.map(sf => ({ id: sf.id, name: sf.name }));
    },
    availableCarsForSubFleet() {
      const currentId = this.subFleetDetail?.id;
      // Wszystkie auta firmy które NIE są w tej konkretnej podflocie (mogą być w innej lub w głównej).
      // `this.cars` jest stronicowane, więc jako fallback dorzucamy też auta z innych podflot.
      const fromTable = (this.cars || [])
        .filter(c => c.subFleetId !== currentId)
        .map(c => ({
          id: c.id,
          label: `${c.plate || ''} — ${c.make || ''} ${c.model || ''}${c.subFleetName ? ' (' + c.subFleetName + ')' : ' (główna flota)'}`,
          subFleetId: c.subFleetId,
        }));
      const seen = new Set(fromTable.map(c => c.id));
      // Dorzuć auta z innych podflot (mogą nie być w bieżącej stronie tabeli)
      const fromOtherSubFleets = (this.subFleets || [])
        .filter(sf => sf.id !== currentId)
        .flatMap(sf => (sf.cars || []).map(c => ({
          id: c.id,
          label: `${c.plate || ''} — ${c.make || ''} ${c.model || ''} (${sf.name})`,
          subFleetId: sf.id,
        })))
        .filter(c => !seen.has(c.id));
      return [...fromTable, ...fromOtherSubFleets].sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    },
  },
  async created() {
    await this.getDriversSelect();
    await this.getSubcompanySelect();
    await this.getFuelCards();
    await this.fetchSubFleets();
    await this.initialize();
  },
  methods: {
    getHeaders() {
      return { Authorization: "Bearer " + localStorage.getItem("token"), "x-current-page": "fleet" };
    },
    async syncFromBolt() {
      // Krok 1: pobierz z Bolta aktywne auta, których jeszcze nie ma we flocie,
      // i pokaż do wyboru (admin decyduje, które dodać).
      this.boltSyncing = true;
      try {
        const r = await axios({ method: "get", url: process.env.VUE_APP_ROOT_API + "bolt/sync-vehicles/preview", headers: this.getHeaders() });
        const d = r.data?.data || {};
        this.boltVehicles = (d.boltOnly || []).filter((v) => v.state === "active");
        this.boltSelected = this.boltVehicles.slice(); // domyślnie wszystkie zaznaczone
        if (!this.boltVehicles.length) {
          Swal.fire("Brak nowych aut", "Nie znaleziono aktywnych aut z Bolta do dodania (wszystkie już są we flocie).", "info");
        } else {
          this.boltDepartmentId = null;
          this.boltDialog = true;
        }
      } catch (e) {
        Swal.fire("Błąd", e.response?.data?.message || "Nie udało się pobrać aut z Bolta. Sprawdź, czy integracja Bolt jest skonfigurowana i aktywna.", "error");
      }
      this.boltSyncing = false;
    },
    async confirmBoltImport() {
      if (!this.boltSelected.length) return;
      this.boltCreating = true;
      try {
        const ids = this.boltSelected.map((v) => v.id);
        // Tworzy TYLKO wybrane auta we flocie...
        const bolt = await axios({ method: "post", url: process.env.VUE_APP_ROOT_API + "bolt/sync-vehicles/create-from-bolt", headers: this.getHeaders(), data: { boltVehicleIds: ids, departmentId: this.boltDepartmentId || null } });
        const bd = bolt.data?.data || {};
        // ...i wrzuca flotę do wykazu pojazdów.
        const reg = await axios({ method: "post", url: process.env.VUE_APP_ROOT_API + "registry/vehicles/sync", headers: this.getHeaders() });
        const rd = reg.data?.data || {};
        this.boltDialog = false;
        Swal.fire({
          title: (bd.created ?? 0) > 0 ? "Gotowe" : "Uwaga",
          html: `Dodano do floty <b>${bd.created ?? 0}</b> aut (pominięto ${bd.skipped ?? 0}` +
                (bd.errors && bd.errors.length ? `, błędy: ${bd.errors.length}` : "") + `).<br>` +
                `Do wykazu pojazdów dodano <b>${rd.created ?? 0}</b> (pominięto: ${rd.skipped ?? 0}).`,
          icon: (bd.created ?? 0) > 0 ? "success" : "warning",
        });
        this.initialize();
      } catch (e) {
        Swal.fire("Błąd", e.response?.data?.message || "Błąd importu aut z Bolta.", "error");
      }
      this.boltCreating = false;
    },
    async pairDriversFromBolt() {
      // Paruje auta z kierowcami wg danych Bolta i przenosi je do FLOTY KIEROWCÓW.
      // Ustawia carId tylko tam, gdzie kierowca nie ma jeszcze auta.
      const confirm = await Swal.fire({
        title: "Sparować auta z kierowcami?",
        html: "Dla każdego auta z Bolta ustawię kierowcę, który nim jeździ (wg Bolta), i przeniosę auto do floty kierowców.<br>" +
              "Przypiszę <b>tylko tam, gdzie kierowca nie ma jeszcze auta</b> — istniejących przypisań nie ruszam.",
        icon: "question", showCancelButton: true,
        confirmButtonText: "Sparuj", cancelButtonText: "Anuluj",
      });
      if (!confirm.isConfirmed) return;
      this.boltPairing = true;
      try {
        const r = await axios({ method: "post", url: process.env.VUE_APP_ROOT_API + "bolt/sync-vehicles/pair-drivers", headers: this.getHeaders() });
        const d = r.data?.data || {};
        Swal.fire({
          title: (d.paired ?? 0) > 0 ? "Gotowe" : "Nic do sparowania",
          html: `Sparowano i przeniesiono do floty kierowców: <b>${d.paired ?? 0}</b>.<br>` +
                `Pominięto: kierowca ma już auto — ${d.skippedDriverHasCar ?? 0}, ` +
                `brak auta o tej tablicy — ${d.skippedNoCar ?? 0}, ` +
                `auto Bolta bez naszego kierowcy — ${d.skippedNoDriver ?? 0}.`,
          icon: (d.paired ?? 0) > 0 ? "success" : "info",
        });
        this.initialize();
      } catch (e) {
        Swal.fire("Błąd", e.response?.data?.message || "Nie udało się sparować aut z kierowcami.", "error");
      }
      this.boltPairing = false;
    },
    async fetchArchive() {
      this.archiveLoading = true;
      try {
        const res = await axios({
          method: "get",
          url:
            process.env.VUE_APP_ROOT_API +
            `cars/archive?take=10000&skip=1&search=${this.archiveSearch}`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.archiveCars = res.data.data.result.map((car) => ({
          id: car.id,
          make: car.make,
          model: car.model,
          plate: car.plate,
          sideNumber:
            typeof car.sideNumber === "object" && car.sideNumber !== null
              ? car.sideNumber.value || car.sideNumber.taxiNo || JSON.stringify(car.sideNumber)
              : car.sideNumber || "-",
        }));
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", translateError(e), "error");
      } finally {
        this.archiveLoading = false;
      }
    },
    async restoreCar(id) {
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "cars/archive/" + id + "/restore",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        await this.fetchArchive();
        await this.initialize();
        Swal.fire({ title: "Przywrócono", icon: "success", timer: 1400, showConfirmButton: false });
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", translateError(e), "error");
      }
    },
    getSubFleetIdsRecursive(id) {
      const ids = [id];
      this.subFleets.filter(sf => sf.parentId === id).forEach(child => {
        ids.push(...this.getSubFleetIdsRecursive(child.id));
      });
      return ids;
    },
    // ===== Znajdź / przywróć auto po tablicy (gdy zniknęło z list) =====
    openPlateFinder() {
      this.plateFinder = { open: true, query: '', results: [], searched: false, searching: false, restoringId: null, movingId: null };
    },
    async searchByPlate() {
      const plate = (this.plateFinder.query || '').trim();
      if (!plate) return;
      this.plateFinder.searching = true;
      try {
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'cars/find-by-plate?plate=' + encodeURIComponent(plate),
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.plateFinder.results = (res.data && res.data.data) || [];
        this.plateFinder.searched = true;
      } catch (e) {
        console.log(e);
        Swal.fire('Błąd', translateError(e), 'error');
      } finally {
        this.plateFinder.searching = false;
      }
    },
    async moveToDriverFleet(car) {
      this.plateFinder.movingId = car.id;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'cars/make-private/' + car.id,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        Swal.fire({ title: 'Przeniesiono do floty kierowców', icon: 'success', timer: 1600, showConfirmButton: false });
        await this.searchByPlate();
        await this.initialize();
      } catch (e) {
        console.log(e);
        Swal.fire('Błąd', translateError(e), 'error');
      } finally {
        this.plateFinder.movingId = null;
      }
    },
    async restoreAsCompany(car) {
      this.plateFinder.restoringId = car.id;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'cars/restore-as-company/' + car.id,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        Swal.fire({ title: 'Przywrócono jako auto firmowe', icon: 'success', timer: 1600, showConfirmButton: false });
        await this.searchByPlate();
        await this.initialize();
      } catch (e) {
        console.log(e);
        Swal.fire('Błąd', translateError(e), 'error');
      } finally {
        this.plateFinder.restoringId = null;
      }
    },
    // ===== Inspekcja auta =====
    formatInspDate(d) {
      return d ? new Date(d).toLocaleDateString('pl-PL') : '—';
    },
    inspStatusLabel(s) {
      return { PENDING_REVIEW: 'Do przeglądu', ACCEPTED: 'Zaakceptowana', FLAGGED: 'Usterka' }[s] || s || '—';
    },
    inspStatusColor(s) {
      return { PENDING_REVIEW: 'warning', ACCEPTED: 'success', FLAGGED: 'error' }[s] || 'grey';
    },
    async openInspection(car) {
      this.inspection = { open: true, car, status: null, history: [], showPerform: false, perform: {}, performNote: '', performOdometer: null, performPhotos: [], performPreviews: [], photoUrls: {}, saving: false, reviewingId: null, requesting: false };
      await this.fetchInspectionData(car.id);
    },
    async fetchInspectionData(carId) {
      try {
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'car-inspections/car/' + carId,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        const d = (res.data && res.data.data) || {};
        this.inspection.status = d.status || null;
        this.inspection.history = d.inspections || [];
        this.loadInspectionPhotos();
      } catch (e) {
        console.log(e);
        Swal.fire('Błąd', translateError(e), 'error');
      }
    },
    async requestInspection() {
      if (!this.inspection.car) return;
      this.inspection.requesting = true;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'car-inspections/request/' + this.inspection.car.id,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        Swal.fire({ title: 'Zażądano inspekcji', text: 'Kierowca zobaczy inspekcję do wykonania w aplikacji.', icon: 'success', timer: 1800, showConfirmButton: false });
        await this.fetchInspectionData(this.inspection.car.id);
      } catch (e) {
        Swal.fire('Błąd', translateError(e), 'error');
      } finally {
        this.inspection.requesting = false;
      }
    },
    inspMileage(insp) {
      return insp && insp.checklist && insp.checklist.odometer ? insp.checklist.odometer : null;
    },
    // Mapa wartości oceny → etykieta + kolor. Obsługuje też stare inspekcje
    // zapisane jako boolean (true = OK, false = brak).
    ratingMeta(v) {
      if (v === 'VERY_GOOD') return { label: 'Bardzo dobry', color: 'green darken-1' };
      if (v === 'GOOD') return { label: 'Dobry', color: 'amber darken-2' };
      if (v === 'BAD') return { label: 'Zły', color: 'red darken-1' };
      if (v === true) return { label: 'OK', color: 'green darken-1' };
      if (v === false) return { label: 'Nie', color: 'grey' };
      return null;
    },
    // Lista ocenionych pozycji danej inspekcji (pomija przebieg/odometer).
    inspRatings(insp) {
      const cl = (insp && insp.checklist) || {};
      const out = [];
      this.inspectionItems.forEach((it) => {
        if (cl[it.key] === undefined || cl[it.key] === null) return;
        const meta = this.ratingMeta(cl[it.key]);
        if (meta) out.push({ name: it.label, label: meta.label, color: meta.color });
      });
      return out;
    },
    // Zdjęcia są serwowane przez authed POST download-file/:id → pobieramy je
    // jako blob i tworzymy lokalne URL-e (Vue.set, by reaktywnie odświeżyć).
    async loadInspectionPhotos() {
      const ids = [];
      this.inspection.history.forEach((insp) => (insp.photos || []).forEach((id) => { if (id && !this.inspection.photoUrls[id]) ids.push(id); }));
      for (const id of ids) {
        try {
          const res = await axios({
            method: 'post',
            url: process.env.VUE_APP_ROOT_API + 'aws/download-file/' + id,
            responseType: 'blob',
            headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          });
          this.$set(this.inspection.photoUrls, id, window.URL.createObjectURL(res.data));
        } catch (e) { /* pomijamy pojedyncze błędy zdjęć */ }
      }
    },
    onPerformPhotos(e) {
      const files = Array.from(e.target.files || []);
      files.forEach((f) => { this.inspection.performPhotos.push(f); this.inspection.performPreviews.push(URL.createObjectURL(f)); });
      e.target.value = '';
    },
    removePerformPhoto(i) {
      URL.revokeObjectURL(this.inspection.performPreviews[i]);
      this.inspection.performPhotos.splice(i, 1);
      this.inspection.performPreviews.splice(i, 1);
    },
    async submitAdminInspection() {
      this.inspection.saving = true;
      try {
        const checklist = {};
        this.inspectionItems.forEach((it) => { checklist[it.key] = this.inspection.perform[it.key] || null; });
        const fd = new FormData();
        fd.append('carId', this.inspection.car.id);
        fd.append('checklist', JSON.stringify(checklist));
        if (this.inspection.performOdometer) fd.append('odometer', String(this.inspection.performOdometer));
        if (this.inspection.performNote) fd.append('note', this.inspection.performNote);
        this.inspection.performPhotos.forEach((f) => fd.append('photos', f, f.name));
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'car-inspections/admin',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token'), 'Content-Type': 'multipart/form-data' },
          data: fd,
        });
        Swal.fire({ title: 'Inspekcja zapisana', icon: 'success', timer: 1400, showConfirmButton: false });
        this.inspection.showPerform = false;
        this.inspection.perform = {};
        this.inspection.performNote = '';
        this.inspection.performOdometer = null;
        this.inspection.performPhotos = [];
        this.inspection.performPreviews = [];
        await this.fetchInspectionData(this.inspection.car.id);
      } catch (e) {
        console.log(e);
        Swal.fire('Błąd', translateError(e), 'error');
      } finally {
        this.inspection.saving = false;
      }
    },
    async reviewInspection(id, decision) {
      this.inspection.reviewingId = id;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'car-inspections/' + id + '/review',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: { decision },
        });
        await this.fetchInspectionData(this.inspection.car.id);
      } catch (e) {
        console.log(e);
        Swal.fire('Błąd', translateError(e), 'error');
      } finally {
        this.inspection.reviewingId = null;
      }
    },
    // ===== Korekta przebiegu auta (head admin) =====
    openOdoEdit(car) {
      this.odoEdit = { open: true, car, value: car.odometer != null ? car.odometer : null, saving: false };
    },
    async saveOdo() {
      if (this.odoEdit.value === null || this.odoEdit.value === '' || Number(this.odoEdit.value) < 0) {
        Swal.fire('Błąd', 'Podaj poprawny przebieg (km).', 'warning');
        return;
      }
      this.odoEdit.saving = true;
      try {
        await axios({
          method: 'patch',
          url: process.env.VUE_APP_ROOT_API + 'cars/' + this.odoEdit.car.id + '/odometer',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: { odometer: Number(this.odoEdit.value) },
        });
        // Aktualizacja w miejscu na liście floty.
        this.odoEdit.car.odometer = Number(this.odoEdit.value);
        this.odoEdit.open = false;
        Swal.fire({ title: 'Przebieg zapisany', icon: 'success', timer: 1300, showConfirmButton: false });
      } catch (e) {
        console.log(e);
        Swal.fire('Błąd', translateError(e), 'error');
      } finally {
        this.odoEdit.saving = false;
      }
    },
    // ===== Korekta VIN auta (head admin) =====
    async editVin(car) {
      const { value: vin, isConfirmed } = await Swal.fire({
        title: 'Korekta VIN',
        input: 'text',
        inputLabel: `Auto: ${car.make} ${car.model} (${car.plate})`,
        inputValue: car.vin || '',
        inputPlaceholder: 'np. WVWZZZ1KZAW123456 (puste = wyczyść)',
        inputAttributes: { maxlength: 20, style: 'text-transform:uppercase' },
        showCancelButton: true,
        confirmButtonText: 'Zapisz',
        cancelButtonText: 'Anuluj',
      });
      if (!isConfirmed) return;
      try {
        await axios({
          method: 'patch',
          url: process.env.VUE_APP_ROOT_API + 'cars/' + car.id + '/vin',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: { vin: (vin || '').trim() },
        });
        car.vin = (vin || '').trim().toUpperCase();
        Swal.fire({ title: 'VIN zapisany', icon: 'success', timer: 1300, showConfirmButton: false });
      } catch (e) {
        console.log(e);
        Swal.fire('Błąd', translateError(e), 'error');
      }
    },
    async toggleLoading(type) {
      const toggle = type;
      localStorage.setItem("loading", toggle);
      window.dispatchEvent(
        new CustomEvent("toggleLoading", {
          detail: {
            loading: localStorage.getItem("loading"),
            overlay: localStorage.getItem("loading"),
          },
        })
      );
    },
    async endRentShowModal(id){
      this.currentCarId = id;
      this.$set(this.endRent, 'endTime', dayjs().format("HH:mm"));
      try {
        const dep = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "cars/" + id,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });

        this.currentCarFuelType = dep.data.data.fuelType || 'BENZYNA';
        if (dep.data.data.drivers.length > 0) {
          this.driversEndRent.push({
            id: dep.data.data.drivers[0].id,
            name: `${dep.data.data.drivers[0].firstName} ${dep.data.data.drivers[0].lastName}`,
          });
        }
        if (dep.data.data.secondDrivers.length > 0) {
          this.driversEndRent.push({
            id: dep.data.data.secondDrivers[0].id,
            name: `${dep.data.data.secondDrivers[0].firstName} ${dep.data.data.secondDrivers[0].lastName}`,
          });
        }
        this.dialogCreate = true;
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", translateError(e), "error");
      }
    },
    validateOptionalUUID(value) {
      return value === null || this.validateUUID(value.id || value);
    },
    validateUUID(value) {
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      return !value || uuidRegex.test(value) || "Nieprawidłowy format UUID";
    },
    validateRequired(value) {
      return !!value || "To pole jest wymagane";
    },
    onHistoryRecordTypeChange(type) {
      // Load predefined metadata fields based on the type
      this.predefinedMetadataFields = this.metadataTemplates[type] || [];
      this.predefinedMetadata = {};
    },
    addMetadata() {
      if (this.metadataField.name && this.metadataField.value) {
        this.newHistory.metadata.push({ ...this.metadataField });
        this.metadataField.name = "";
        this.metadataField.value = "";
      }
    },
    removeMetadata(index) {
      this.newHistory.metadata.splice(index, 1);
    },
      async getSubcompanySelect() {
      await this.toggleLoading(true);
      try {
        const subCompanies = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `sub-company`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.subCompanies = subCompanies.data.data.map((it) => {
          return {
            id: it.id,
            name: it.name,
          };
        });
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", translateError(e), "error");
      }
      await this.toggleLoading(false);
    },
    async handleSelection() {
      if (this.selectedTables.length > 2) {
        this.selectedTables.shift(); // Keep only the last two selected
      }
      if (this.selectedTables.length === 2) {
        this.showDialog = true;
        this.comparedTables = [];
        this.comparedTables.push(
          this.carHistory.find((it) => it.id === this.selectedTables[0])
            .metadata
        );
        this.comparedTables.push(
          this.carHistory.find((it) => it.id === this.selectedTables[1])
            .metadata
        );
        console.log(this.comparedTables);
      }
    },
    getRowClass(item, table) {
      const otherTable =
        table === "table1" ? this.comparedTables[1] : this.comparedTables[0];
      const matchingItem = otherTable.find((i) => i.name === item.name);
      console.log(matchingItem);
      if (matchingItem && matchingItem.value !== item.value) {
        return "bg-red"; // Apply red background for differences
      }
      if (matchingItem && matchingItem.value === item.value) {
        return "bg-green"; // Apply red background for differences
      }
      return "bg-orange";
    },
    async saveEndRent() {
      await this.toggleLoading(true);
      try {
        const formData = new FormData();
        this.endRent.photos?.forEach((file) => {
          try {
            formData.append(`file`, file, file.name);
          } catch (e) {
            console.log("Missing Image");
          }
        });
        if (!this.endRent.endTime) {
          await this.toggleLoading(false);
          Swal.fire("Brak godziny", "Podaj godzinę zwrotu auta — decyduje o czynszu za dzień zwrotu i o odpowiedzialności za mandaty.", "warning");
          return;
        }
        // Data + godzina jako pełna chwila (strefa przeglądarki = Polska).
        formData.append(
          "endDate",
          new Date(`${this.endRent.endDate || dayjs().format("YYYY-MM-DD")}T${this.endRent.endTime}:00`).toISOString(),
        );
        formData.append("description", this.endRent.description);
        formData.append("carId", this.currentCarId);
        formData.append("driverId", this.endRent.driverId);
        formData.append(
          "metadata",
          JSON.stringify(
            this.protocol.map((it) => {
              return {
                value: it.checked,
                name: it.label,
              };
            })
          )
        );

        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "cars-history/end-rent",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
          data: formData,
        });
        this.skip = 1;
        await this.toggleLoading(false);
        await this.initialize();
                this.endRent.driverId = null;
        this.endRent.description = null;
        this.endRent.price = null;
        this.endRent.endDate = null;
        this.selectedOption = null;
        this.protocol = this.protocol.map((it) => {
          return {
            checked: false,
            label: it.label,
          };
        });
        this,this.endRent.photos = []
        this.dialogCreate = false;
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", translateError(e), "error");
      }
      await this.toggleLoading(false);
    },
    async addHistory() {
      await this.toggleLoading(true);
      if (!this.carHistoryValid) return;
      // Combine predefined metadata with additional metadata
      const combinedMetadata = [
        ...this.newHistory.metadata,
        ...Object.entries(this.predefinedMetadata).map(([key, value]) => ({
          name: key,
          value,
        })),
      ];
      const formData = new FormData();
      try {
        formData.append(
          "file",
          this.newHistory.image,
          this.newHistory.image.name
        );
      } catch (e) {
        console.log("Missing Image");
      }
      formData.append("carId", this.currentCarId);
      formData.append("metadata", JSON.stringify(combinedMetadata));
      formData.append("description", this.newHistory.description);
      formData.append("historyRecordType", this.newHistory.historyRecordType);
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "cars-history",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
          data: formData,
        });
        // Reset form
        this.newHistory = {
          description: "",
          historyRecordType: "",
          image: null,
          metadata: [],
        };
        this.predefinedMetadataFields = [];
        this.predefinedMetadata = {};
        await this.historyCar(this.currentCarId);
      } catch (e) {
        await this.toggleLoading(false);
        console.log(e);
        Swal.fire("Błąd", translateError(e), "error");
      }
      await this.toggleLoading(false);
    },
    async checkCar() {
      await this.toggleLoading(true);
      try {
        const car = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `cars/check`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          params: {
            driverId: this.formData.driverId.id,
            carId: this.currentCarId,
            triggerType: "car",
          },
        });
        if (car.data.data.msg) {
          Swal.fire("Błąd", car.data.data.msg, "warning");
        }
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", translateError(e), "error");
      }
      await this.toggleLoading(false);
    },
    async checkCar2() {
      await this.toggleLoading(true);
      try {
        const car = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `cars/check`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          params: {
            driverId: this.formData.secondDriverId.id,
            carId: this.currentCarId,
            triggerType: "car",
          },
        });
        if (car.data.data.msg) {
          Swal.fire("Błąd", car.data.data.msg, "warning");
        }
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", translateError(e), "error");
      }
      await this.toggleLoading(false);
    },
    async historyCar(id) {
      location.href = `fleet-details?id=${id}`;

      // await this.toggleLoading(true);
      // try {
      //   const car = await axios({
      //     method: "get",
      //     url: process.env.VUE_APP_ROOT_API + "cars/" + id,
      //     headers: {
      //       Authorization: "Bearer " + localStorage.getItem("token"),
      //     },
      //   });
      //   this.currentDrivers = []
      //   if(car.data.data.drivers.length >0 ){
      //     this.currentDrivers.push({
      //       id: car.data.data.drivers[0].id,
      //       name: `${car.data.data.drivers[0].firstName} ${car.data.data.drivers[0].lastName}`
      //     })
      //   }
      //   if(car.data.data.secondDrivers.length >0 ){
      //     this.currentDrivers.push({
      //       id: car.data.data.secondDrivers[0].id,
      //       name: `${car.data.data.secondDrivers[0].firstName} ${car.data.data.secondDrivers[0].lastName}`
      //     })
      //   }
      //   const carHistory = await axios({
      //     method: "get",
      //     url: process.env.VUE_APP_ROOT_API + "cars-history/" + id,
      //     headers: {
      //       Authorization: "Bearer " + localStorage.getItem("token"),
      //     },
      //   });
      //   this.plate = car.data.data.plate;
      //   this.currentCarId = id;
      //   this.carHistory = carHistory.data.data.map((it) => {
      //     return {
      //       id: it.id,
      //       date: dayjs(it.createdAt).format("YYYY-MM-DD HH:mm"),
      //       description: it.description,
      //       type: this.newHistory.historyRecordType,
      //       metadata:
      //         typeof it.metadata === "string"
      //           ? JSON.parse(it.metadata)
      //           : it.metadata,
      //       image: it.files[0]?.id || null, // Image file object or path
      //       color: "blue", // Default color for new records
      //       icon: "mdi-plus",
      //     };
      //   });
      //   await this.toggleLoading(false);
      // } catch (e) {
      //   await this.toggleLoading(false);
      //   console.log(e);
      //   Swal.fire("Błąd", e.response.data.message.toString(), "error");
      // }
      // this.dialogHistory = true;
    },
    async submitForm() {
      if (!this.valid) return;
      try {
        await this.toggleLoading(true);
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "cars",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          data: {
            ...this.formData,
            cardId: this.formData.cardId?.id,
            advance: parseInt(this.formData.advance),
            rentPrice: parseInt(this.formData.rentPrice),
            odometer: parseInt(this.formData.odometer),
            previousServiceOdometer: parseInt(this.formData.previousServiceOdometer),
            productionYear: parseInt(this.formData.productionYear),
            id: this.currentCarId || undefined,
          },
        });
        this.closeDialog();
        await this.toggleLoading(false);
        await this.initialize();
      } catch (e) {
        await this.toggleLoading(false);
        console.log(e);
        Swal.fire("Błąd", translateError(e), "error");
      }
    },
    closeDialog() {
      this.dialog = false;
      this.currentCarId = undefined;
      this.formData = {
        make: "",
        model: "",
        color: "",
        plate: "",
        isCompanyCar: true,
        productionYear: null,
        lastService: null,
        insuranceValidTo: null,
        inspectionValidTo: null,
        wrapType: null,
        advance: null,
        rentPrice: null,
        odometer: null,
        previousServiceOdometer: null,
        sideNumber: null,
        insurancePolicyNumber: null,
        insurerName: null,
        fuelType: "BENZYNA",
        cardId: null,
        departmentId: null,
      };
      this.$refs.form.resetValidation();
    },
    async infoCar(id) {
      await this.toggleLoading(true);
      try {
        const car = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "cars/" + id,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.formData = car.data.data;
        this.currentCarId = car.data.data.id;
        this.formData.rentPrice = car.data.data.rentPrice / 100;
        this.formData.advance = car.data.data.advance / 100;
        this.formData.cardId =
          car.data.data.fuelCards.length > 0
            ? car.data.data.fuelCards[0].id
            : null;
        this.formData.isCompanyCar = car.data.data.isCompanyCar;
        this.formData.inspectionValidTo = dayjs(
          car.data.data.inspectionValidTo
        ).format("YYYY-MM-DD");
        this.formData.insuranceValidTo = dayjs(
          car.data.data.insuranceValidTo
        ).format("YYYY-MM-DD");
        this.formData.lastService = dayjs(car.data.data.lastService).format(
          "YYYY-MM-DD"
        );
        this.dialog = true;
        await this.toggleLoading(false);
      } catch (e) {
        await this.toggleLoading(false);
        console.log(e);
        Swal.fire("Błąd", translateError(e), "error");
      }
    },
    async getDriversSelect() {
      await this.toggleLoading(true);
      try {
        const drivers = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `drivers?take=100&skip=1`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.drivers = drivers.data.data.result.map((it) => {
          return {
            id: it.id,
            name: `${it.firstName} ${it.lastName}`,
          };
        });
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", translateError(e), "error");
      }
      await this.toggleLoading(false);
    },
    async getFuelCards() {
      await this.toggleLoading(true);
      try {
        const fuelCards = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `fuel-cards?take=100&skip=1`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.fuelCards = fuelCards.data.data.result.map((it) => {
          return {
            id: it.id,
            name: `${it.provider}-${it.number}`,
          };
        });
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", translateError(e), "error");
      }
      await this.toggleLoading(false);
    },
    async initialize() {
      this.loading = true;
      try {
        // Pobierz wszystkie auta (bez paginacji serwerowej — sortowanie
        // i paginacja są lokalne, dzięki temu sortowanie działa po każdej
        // kolumnie, w tym po relacjach: kierowcy, podflota).
        const cars = await axios({
          method: "get",
          url:
            process.env.VUE_APP_ROOT_API +
            `cars?take=10000&skip=1&search=${this.search}`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.cars = cars.data.data.result.map((car) => {
          return {
            model: car.model,
            make: car.make,
            plate: car.plate,
            id: car.id,
            sideNumber: typeof car.sideNumber === 'object' && car.sideNumber !== null ? (car.sideNumber.value || car.sideNumber.taxiNo || JSON.stringify(car.sideNumber)) : (car.sideNumber || '-'),
            subFleetId: car.subFleetId,
            subFleetName: car.subFleet?.name || null,
            firstDriver:
              car.drivers.length > 0
                ? car.drivers[0].firstName + " " + car.drivers[0].lastName
                : "",
            secondDriver:
              car.secondDrivers.length > 0
                ? car.secondDrivers[0]?.firstName +
                  " " +
                  car.secondDrivers[0]?.lastName
                : "",
          };
        });
        this.totalItems = cars.data.data.count;
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", translateError(e), "error");
      }
      this.loading = false;
    },
    async deleteCar(id) {
      Swal.fire({
        title: "Napewno usunąć?",
        text: "",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Tak",
        cancelButtonText: "Anuluj",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await axios({
              method: "delete",
              url: process.env.VUE_APP_ROOT_API + "cars/" + id,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            });
            await this.initialize();
          } catch (e) {
            console.log(e);
            Swal.fire("Błąd", translateError(e), "error");
          }
        }
      });
    },
    async downloadImage(id) {
      const file = await axios({
        method: "post",
        url: process.env.VUE_APP_ROOT_API + "aws/download-file/" + id,
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
        responseType: "blob",
      });
      let blob = new Blob([file.data], { type: file.data.type }),
        url = window.URL.createObjectURL(blob);
      window.open(url);
    },
    // Sub-fleet methods
    async fetchSubFleets() {
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "sub-fleet",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.subFleets = res.data.data;
      } catch (e) {
        console.error("Failed to fetch sub-fleets:", e);
      }
    },
    openSubFleetDialog(item) {
      if (item) {
        this.subFleetEdit = { id: item.id, name: item.name, parentId: item.parentId || null };
      } else {
        this.subFleetEdit = { id: null, name: "", parentId: null };
      }
      this.subFleetDialog = true;
    },
    async saveSubFleet() {
      try {
        if (this.subFleetEdit.id) {
          await axios({
            method: "patch",
            url: process.env.VUE_APP_ROOT_API + "sub-fleet/" + this.subFleetEdit.id,
            headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            data: { name: this.subFleetEdit.name },
          });
        } else {
          await axios({
            method: "post",
            url: process.env.VUE_APP_ROOT_API + "sub-fleet",
            headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            data: { name: this.subFleetEdit.name, parentId: this.subFleetEdit.parentId || undefined },
          });
        }
        this.subFleetDialog = false;
        await this.fetchSubFleets();
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
    },
    async deleteSubFleet(id) {
      const result = await Swal.fire({
        title: "Usunąć podflotę?",
        text: "Auta zostaną odpięte z podfloty",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Tak",
        cancelButtonText: "Anuluj",
      });
      if (result.isConfirmed) {
        try {
          await axios({
            method: "delete",
            url: process.env.VUE_APP_ROOT_API + "sub-fleet/" + id,
            headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          });
          if (this.activeSubFleetId === id) this.activeSubFleetId = null;
          await this.fetchSubFleets();
        } catch (e) {
          Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
        }
      }
    },
    onSubFleetSelect(ids) {
      this.activeSubFleetId = ids.length > 0 ? ids[0] : null;
    },
    clearSubFleetFilter() {
      this.activeSubFleetId = null;
    },
    openAssignCarDialog(carId) {
      this.assignCarId = carId;
      const car = this.cars.find(c => c.id === carId);
      this.assignCarSubFleetId = car ? car.subFleetId : null;
      this.assignCarDialog = true;
    },
    async assignCarToSubFleet() {
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "sub-fleet/assign-car",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: { carId: this.assignCarId, subFleetId: this.assignCarSubFleetId || undefined },
        });
        this.assignCarDialog = false;
        await this.fetchSubFleets();
        await this.initialize();
      } catch (e) {
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
    },
    async addCarToCurrentSubFleet() {
      if (!this.subFleetAddCarId || !this.subFleetDetail?.id) return;
      this.subFleetAddCarLoading = true;
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "sub-fleet/assign-car",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: { carId: this.subFleetAddCarId, subFleetId: this.subFleetDetail.id },
        });
        const carId = this.subFleetAddCarId;
        this.subFleetAddCarId = null;
        await this.fetchSubFleets();
        await this.initialize();
        // Odśwież listę aut w otwartym dialogu
        await this.openSubFleetDetailDialog(this.subFleetDetail.id);
        Swal.fire({ icon: "success", title: "Dodano", text: "Auto zostało przypisane do podfloty.", timer: 1500, showConfirmButton: false });
        return carId;
      } catch (e) {
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Nie udało się przypisać auta", "error");
      } finally {
        this.subFleetAddCarLoading = false;
      }
    },
    async removeCarFromCurrentSubFleet(carId) {
      const res = await Swal.fire({
        title: "Odpiąć auto z podfloty?",
        text: "Auto wróci do głównej floty.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Tak",
        cancelButtonText: "Anuluj",
      });
      if (!res.isConfirmed) return;
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "sub-fleet/assign-car",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: { carId, subFleetId: null },
        });
        await this.fetchSubFleets();
        await this.initialize();
        await this.openSubFleetDetailDialog(this.subFleetDetail.id);
      } catch (e) {
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Nie udało się odpiąć auta", "error");
      }
    },
    async deleteCarHistory(id) {
      Swal.fire({
        title: "Napewno usunąć?",
        text: "",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Tak",
        cancelButtonText: "Anuluj",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await axios({
              method: "delete",
              url:
                process.env.VUE_APP_ROOT_API +
                "cars-history/" +
                this.currentCarId +
                "/" +
                id,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            });
            await this.historyCar(this.currentCarId);
          } catch (e) {
            console.log(e);
            Swal.fire("Błąd", translateError(e), "error");
          }
        }
      });
    },
    async openSubFleetDetailDialog(id) {
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "sub-fleet/" + id + "/details",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        const payload = res.data?.data || res.data || {};
        this.subFleetDetail = payload;
        this.subFleetDetailCars = (payload.cars || []).map(car => ({
          ...car,
          driverName: car.drivers?.[0] ? `${car.drivers[0].firstName} ${car.drivers[0].lastName}` : '-',
        }));
        this.subFleetDetailTab = 0;
        this.settlementResult = null;
        this.subFleetAddCarId = null;
        this.subFleetDetailDialog = true;
      } catch (e) {
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Nie można załadować podfloty", "error");
      }
    },
    async saveSubFleetDetail() {
      this.subFleetDetailSaving = true;
      try {
        await axios({
          method: "patch",
          url: process.env.VUE_APP_ROOT_API + "sub-fleet/" + this.subFleetDetail.id,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: {
            ownerName: this.subFleetDetail.ownerName,
            ownerCompanyName: this.subFleetDetail.ownerCompanyName,
            ownerAddress: this.subFleetDetail.ownerAddress,
            ownerPhone: this.subFleetDetail.ownerPhone,
            ownerEmail: this.subFleetDetail.ownerEmail,
            ownerNip: this.subFleetDetail.ownerNip,
            ownerPesel: this.subFleetDetail.ownerPesel,
            ownerBankAccount: this.subFleetDetail.ownerBankAccount,
            status: this.subFleetDetail.status,
            feeType: this.subFleetDetail.feeType,
            feeValue: parseInt(this.subFleetDetail.feeValue) || 0,
            feeFixedBase: parseInt(this.subFleetDetail.feeFixedBase) || 0,
            feePeriod: this.subFleetDetail.feePeriod,
            feeDayOfMonth: parseInt(this.subFleetDetail.feeDayOfMonth) || 1,
          },
        });
        Swal.fire("Zapisano", "Dane podfloty zaktualizowane", "success");
        this.fetchSubFleets();
      } catch (e) {
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
      this.subFleetDetailSaving = false;
    },
    async calculateSettlement() {
      this.settlementLoading = true;
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "sub-fleet/" + this.subFleetDetail.id + "/settlement?year=" + this.settlementYear + "&month=" + this.settlementMonth,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.settlementResult = res.data?.data || res.data;
      } catch (e) {
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
      this.settlementLoading = false;
    },
  },
};
</script>
<style scoped>
/* === Driver-app aesthetic for admin Fleet === */
.fleet-page {
  background: #181C28;
  min-height: calc(100vh - 56px);
  padding: 16px 16px 40px;
  margin: -24px;
  color: #fff;
}
.fleet-page * { box-sizing: border-box; }

/* Page header */
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 4px 18px;
}
.page-eyebrow {
  font-size: 12px;
  color: #6E7691;
  font-weight: 500;
  text-transform: capitalize;
}
.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #fff;
  margin: 2px 0 0;
  letter-spacing: -0.02em;
}

/* Segmented tabs */
.tabs-pill {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  background: #232838;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 999px;
  padding: 5px;
  width: fit-content;
}
.tab-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: #B0BEC5;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  font-family: inherit;
}
.tab-pill:hover { color: #fff; background: rgba(255,255,255,0.04); }
.tab-pill--active {
  background: #2962FF;
  color: #fff;
  box-shadow: 0 4px 12px rgba(41, 98, 255, 0.35);
}
.tab-pill--active .v-icon { color: #fff !important; }
.tab-pill .v-icon { color: inherit !important; }

/* Filter / table headers */
.filter-icon {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: rgba(41, 98, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.filter-title {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
  flex-grow: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}
.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(41, 98, 255, 0.18);
  border: 1px solid rgba(41, 98, 255, 0.35);
  color: #5478FF;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}
.filter-chip:hover { background: rgba(41, 98, 255, 0.25); }
.filter-chip .v-icon { color: inherit !important; }
.table-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-wrap: wrap;
}

/* Action pills */
.action-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #181C28;
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #fff;
  padding: 8px 14px;
  border-radius: 10px;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition: transform 0.12s ease, background 0.15s ease, border-color 0.15s ease;
  font-family: inherit;
}
.action-pill:hover { transform: translateY(-1px); border-color: rgba(255,255,255,0.18); }
.action-pill:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
.action-pill .v-icon { color: inherit !important; }
.action-pill--primary { background: #2962FF; border-color: #2962FF; box-shadow: 0 4px 12px rgba(41,98,255,0.3); }
.action-pill--info    { background: rgba(66, 165, 245, 0.18); border-color: rgba(66,165,245,0.35); color: #64B5F6; }
.action-pill--success { background: rgba(45, 203, 115, 0.18); border-color: rgba(45,203,115,0.35); color: #6FE89A; }
.action-pill--warning { background: rgba(255, 152, 0, 0.18); border-color: rgba(255,152,0,0.35); color: #FFB74D; }
.action-pill--error   { background: rgba(244, 67, 54, 0.18); border-color: rgba(244,67,54,0.35); color: #FF8A80; }
.action-pill--neutral { background: rgba(255, 255, 255, 0.06); border-color: rgba(255,255,255,0.12); color: #B0BEC5; }

/* Row buttons */
.row-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: transform 0.1s ease;
  font-family: inherit;
  background: rgba(255,255,255,0.06);
  color: #B0BEC5;
}
.row-btn:hover { transform: translateY(-1px); }
.row-btn--warning { background: rgba(255, 152, 0, 0.18); color: #FFB74D; }
.row-btn--error   { background: rgba(244, 67, 54, 0.18); color: #FF8A80; }
.row-btn--info    { background: rgba(66, 165, 245, 0.18); color: #64B5F6; }
.row-btn .v-icon { color: inherit !important; }

/* Search input */
.search-row { padding: 10px 14px 14px; }
.search-input {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #181C28;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 10px 14px;
}
.search-input input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #fff;
  font-size: 14px;
  font-family: inherit;
}
.search-input input::placeholder { color: #6E7691; }

/* === Vuetify deep overrides === */
.fleet-page >>> .v-card {
  background: #232838 !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 18px !important;
  box-shadow: none !important;
  color: #fff !important;
}
.fleet-page >>> .v-card .v-card__title {
  color: #fff !important;
  font-size: 15px !important;
  font-weight: 700 !important;
  padding: 16px 20px 8px !important;
}
.fleet-page >>> .v-card .v-card__title .v-icon { color: #5478FF !important; }

/* Inputs */
.fleet-page >>> .v-text-field--outlined fieldset,
.fleet-page >>> .v-select--outlined fieldset,
.fleet-page >>> .v-textarea--outlined fieldset {
  border-color: rgba(255,255,255,0.1) !important;
}
.fleet-page >>> .v-text-field--outlined .v-input__slot,
.fleet-page >>> .v-select--outlined .v-input__slot,
.fleet-page >>> .v-textarea--outlined .v-input__slot {
  background: #181C28 !important;
  border-radius: 12px !important;
}
.fleet-page >>> .v-label { color: #6E7691 !important; font-size: 13px !important; }
.fleet-page >>> .v-text-field input,
.fleet-page >>> .v-select__selection,
.fleet-page >>> .v-textarea textarea { color: #fff !important; }
.fleet-page >>> .v-input__prepend-inner .v-icon,
.fleet-page >>> .v-input__append-inner .v-icon { color: #6E7691 !important; }

/* Data tables */
.fleet-page >>> .v-data-table {
  background: #232838 !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 18px !important;
  color: #fff !important;
  box-shadow: none !important;
  margin-bottom: 12px;
}
.fleet-page >>> .v-data-table thead tr th {
  background: #1F2434 !important;
  color: #6E7691 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid rgba(255,255,255,0.06) !important;
}
.fleet-page >>> .v-data-table tbody tr { background: #232838 !important; }
.fleet-page >>> .v-data-table tbody tr:hover { background: #2A3042 !important; }
.fleet-page >>> .v-data-table tbody tr td {
  color: #fff !important;
  border-bottom: 1px solid rgba(255,255,255,0.04) !important;
}
.fleet-page >>> .v-data-footer {
  background: #1F2434 !important;
  color: #B0BEC5 !important;
  border-top: 1px solid rgba(255,255,255,0.06) !important;
}

/* End-rent error v-btn (still v-btn) */
.fleet-page >>> .v-data-table tbody td .v-btn.warning {
  background: rgba(255, 152, 0, 0.18) !important;
  color: #FFB74D !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  box-shadow: none !important;
  min-width: 36px !important;
}
.fleet-page >>> .v-data-table tbody td .v-btn.error {
  background: rgba(244, 67, 54, 0.18) !important;
  color: #FF8A80 !important;
  border-radius: 8px !important;
  text-transform: none !important;
  box-shadow: none !important;
  min-width: 36px !important;
}
.fleet-page >>> .v-data-table tbody td .v-btn.info {
  background: rgba(66, 165, 245, 0.18) !important;
  color: #64B5F6 !important;
  border-radius: 8px !important;
  text-transform: none !important;
  box-shadow: none !important;
  min-width: 36px !important;
}
.fleet-page >>> .v-data-table tbody td .v-btn .v-icon { color: inherit !important; }

/* Toolbar (any leftover) */
.fleet-page >>> .v-toolbar { background: transparent !important; color: #fff !important; }
.fleet-page >>> .v-toolbar .v-btn {
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
}

/* Buttons (general) */
.fleet-page >>> .v-btn:not(.v-btn--icon):not(.action-pill):not(.row-btn):not(.tab-pill):not(.back-btn):not(.dialog-close) {
  border-radius: 10px !important;
  text-transform: none !important;
  letter-spacing: -0.01em !important;
  font-weight: 600 !important;
}

/* Dialog cards */
.fleet-page >>> .v-dialog .v-card { background: #232838 !important; }
.fleet-page >>> .v-dialog .v-card .v-card__title { color: #fff !important; }
.fleet-page >>> .v-tabs-items, .fleet-page >>> .v-window { background: transparent !important; }

/* Chips, dividers, progress */
.fleet-page >>> .v-chip { border-radius: 999px !important; font-weight: 700 !important; }
.fleet-page >>> .v-divider { border-color: rgba(255,255,255,0.06) !important; }
.fleet-page >>> .v-progress-linear { background: rgba(255,255,255,0.06) !important; }

/* Compare-rows colored states (used in comparison dialog table) */
.bg-red {
  background-color: rgba(244, 67, 54, 0.20) !important;
  color: #FF8A80 !important;
}
.bg-green {
  background-color: rgba(45, 203, 115, 0.20) !important;
  color: #6FE89A !important;
}
.bg-orange {
  background-color: rgba(255, 152, 0, 0.30) !important;
  color: #FFB74D !important;
}
</style>
